import type { z } from "zod";

import type { unitLengthSchema, unitSlopeSchema } from "./searchParams";

/**
 * UnitLength selects which measurement system the studio renders metrics in:
 * "metric" (kilometers + meters) or "imperial" (miles + feet). It mirrors the
 * user's `length` display preference and derives its union from unitLengthSchema,
 * so core carries a single definition of the measurement-system vocabulary.
 */
export type UnitLength = z.infer<typeof unitLengthSchema>;

/**
 * UnitSlope selects how the studio renders a slope/gradient: "degrees" (the angle
 * in degrees) or "percentage" (the rise-over-run grade in percent). It mirrors the
 * user's `slope` display preference; slopes are stored in degrees, so "percentage"
 * is a `tan` conversion. Its union derives from unitSlopeSchema, so core carries a
 * single definition of the slope-unit vocabulary.
 */
export type UnitSlope = z.infer<typeof unitSlopeSchema>;

/**
 * UnitFormatOptions carries the measurement systems and the BCP 47 locale used to
 * format both the number and its unit. The locale comes from the user's language
 * preference so the decimal separator and the unit label localize together (e.g.
 * "1,5 km" in French, "1.5 km" in English); `length` drives the distance/elevation
 * formatters and `slope` the slope formatter.
 */
export interface UnitFormatOptions {
  length: UnitLength;
  locale: string;
  slope: UnitSlope;
}

/** METERS_PER_KILOMETER converts a meter distance into kilometers. */
const METERS_PER_KILOMETER = 1000;

/** METERS_PER_MILE is the exact number of meters in one international mile. */
const METERS_PER_MILE = 1609.344;

/**
 * KILOMETERS_PER_MILE converts a kilometer-per-hour speed into miles per hour:
 * one mile is 1.609344 km, so dividing a km/h value by this yields mph. It is the
 * single conversion factor the speed display/parse pair shares, so a round trip
 * (km/h → mph → km/h) is exact.
 */
const KILOMETERS_PER_MILE = METERS_PER_MILE / METERS_PER_KILOMETER;

/** FEET_PER_METER is the exact number of feet in one meter. */
const FEET_PER_METER = 3.28084;

/** DEGREES_TO_RADIANS converts an angle in degrees to radians for the grade tangent. */
const DEGREES_TO_RADIANS = Math.PI / 180;

/** CELSIUS_TO_FAHRENHEIT_RATIO is the 9/5 slope of the Celsius→Fahrenheit line. */
const CELSIUS_TO_FAHRENHEIT_RATIO = 9 / 5;

/** CELSIUS_TO_FAHRENHEIT_OFFSET is the 32° intercept added after the ratio scale. */
const CELSIUS_TO_FAHRENHEIT_OFFSET = 32;

/** CENTIMETERS_PER_METER converts a snow depth in meters into centimeters. */
const CENTIMETERS_PER_METER = 100;

/** INCHES_PER_CENTIMETER is the number of inches in one centimeter for imperial snow. */
const INCHES_PER_CENTIMETER = 0.393701;

/**
 * isFiniteNumber narrows an unknown-or-null metric to a real, finite number so
 * the formatters reject null, undefined, and NaN inputs in one place and return
 * the empty-string fallback for any of them.
 */
const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * formatDistance renders a distance given in meters as a localized, unit-suffixed
 * string: metric converts to kilometers, imperial to miles, each rounded to one
 * decimal place. The number and unit are both formatted via Intl.NumberFormat so
 * they localize together. A null, undefined, or NaN input yields an empty string,
 * so a caller can render the result directly without guarding.
 */
export const formatDistance = (
  meters: number | null | undefined,
  { length, locale }: UnitFormatOptions,
): string => {
  if (!isFiniteNumber(meters)) {
    return "";
  }

  const isMetric = length === "metric";
  const value = isMetric ? meters / METERS_PER_KILOMETER : meters / METERS_PER_MILE;

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
    style: "unit",
    unit: isMetric ? "kilometer" : "mile",
    unitDisplay: "short",
  }).format(value);
};

/**
 * formatNavDistance renders a distance in the reader's unit at a turn-by-turn
 * scale: metric shows whole meters below a kilometer and kilometers (two decimals)
 * at or above one; imperial shows whole feet below a tenth of a mile and miles (two
 * decimals) above. The two decimals keep a long distance precise ("8.65 km"); whole
 * meters keep a short leg legible ("300 m" rather than "0.3 km"), where
 * formatDistance — always kilometers/miles to one decimal — rounds it away. A null,
 * undefined, or NaN input yields an empty string, so a caller renders it directly.
 */
export const formatNavDistance = (
  meters: number | null | undefined,
  { length, locale }: UnitFormatOptions,
): string => {
  if (!isFiniteNumber(meters)) {
    return "";
  }

  const render = (
    value: number,
    unit: "foot" | "kilometer" | "meter" | "mile",
    digits: number,
  ): string =>
    new Intl.NumberFormat(locale, {
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
      style: "unit",
      unit,
      unitDisplay: "short",
    }).format(value);

  if (length === "metric") {
    return meters < METERS_PER_KILOMETER
      ? render(Math.round(meters), "meter", 0)
      : render(meters / METERS_PER_KILOMETER, "kilometer", 2);
  }

  const miles = meters / METERS_PER_MILE;

  return miles < 0.1
    ? render(Math.round(meters * FEET_PER_METER), "foot", 0)
    : render(miles, "mile", 2);
};

/**
 * parseDistanceToMeters converts a distance typed in the reader's length unit
 * (kilometers for metric, miles for imperial) back into the meters the wire
 * carries. It is the inverse of formatDistance, reusing the same conversion
 * constants so a value formatted and re-parsed round-trips, and is the helper
 * the suggest distance control reads its NumberInput through so the session
 * always holds meters regardless of the displayed unit.
 */
export const parseDistanceToMeters = (value: number, length: UnitLength): number =>
  length === "metric" ? value * METERS_PER_KILOMETER : value * METERS_PER_MILE;

/**
 * formatElevation renders an elevation given in meters as a localized,
 * unit-suffixed string: metric keeps meters as-is, imperial converts to feet,
 * each rounded to a whole number (sub-meter precision is noise for elevation).
 * The number and unit are both formatted via Intl.NumberFormat so they localize
 * together. A null, undefined, or NaN input yields an empty string, so a caller
 * can render the result directly without guarding.
 */
export const formatElevation = (
  meters: number | null | undefined,
  { length, locale }: UnitFormatOptions,
): string => {
  if (!isFiniteNumber(meters)) {
    return "";
  }

  const isMetric = length === "metric";
  const value = isMetric ? meters : meters * FEET_PER_METER;

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    style: "unit",
    unit: isMetric ? "meter" : "foot",
    unitDisplay: "short",
  }).format(value);
};

/**
 * parseAscentToMeters converts an elevation gain typed in the reader's length
 * unit (meters for metric, feet for imperial) back into the meters the wire
 * carries. It is the inverse of formatElevation, reusing the same FEET_PER_METER
 * factor so a value formatted and re-parsed round-trips, and is the helper the
 * suggest ascent control reads its NumberInput through so the session always
 * holds meters regardless of the displayed unit.
 */
export const parseAscentToMeters = (value: number, length: UnitLength): number =>
  length === "metric" ? value : value / FEET_PER_METER;

/**
 * UnitSpeed selects which unit the studio renders a travel speed in: "kmh"
 * (kilometers per hour) or "mph" (miles per hour). Speed is a length rate, so it
 * has no preference of its own — it follows the user's `length` choice (metric →
 * km/h, imperial → mph) through speedUnitForLength. The WIRE value is always km/h
 * regardless of the display unit.
 */
export type UnitSpeed = "kmh" | "mph";

/**
 * speedUnitForLength derives the speed display unit from the measurement system:
 * an imperial reader sees mph, everyone else km/h. Speed carries no standalone
 * preference, so this is the single place the length choice maps onto the speed
 * unit — the speed formatter and the editor's speed control both resolve through
 * it so they cannot drift.
 */
export const speedUnitForLength = (length: UnitLength): UnitSpeed =>
  length === "imperial" ? "mph" : "kmh";

/**
 * kilometersPerHourToDisplay converts a km/h wire speed into the value shown in
 * the reader's speed unit: km/h passes through, mph divides by the miles-per-km
 * factor. It is the inverse of displayToKilometersPerHour, so a value typed in the
 * display unit and converted back to km/h round-trips exactly. A null input yields
 * null so the editor can seed an empty "use the activity default" control.
 */
export const kilometersPerHourToDisplay = (kmh: number | null, unit: UnitSpeed): number | null => {
  if (kmh === null) {
    return null;
  }

  return unit === "mph" ? kmh / KILOMETERS_PER_MILE : kmh;
};

/**
 * displayToKilometersPerHour converts a value typed in the reader's speed unit
 * back into the km/h the wire carries: km/h passes through, mph multiplies by the
 * miles-per-km factor. It is the inverse of kilometersPerHourToDisplay. A null
 * input (the control cleared to "use the activity default") yields null so the
 * caller persists a null speed.
 */
export const displayToKilometersPerHour = (
  value: number | null,
  unit: UnitSpeed,
): number | null => {
  if (value === null) {
    return null;
  }

  return unit === "mph" ? value * KILOMETERS_PER_MILE : value;
};

/**
 * formatSpeed renders a travel speed given in km/h (the wire unit) as a localized,
 * unit-suffixed string in the reader's speed unit: metric keeps km/h, imperial
 * converts to mph, each rounded to one decimal place. The number and unit are both
 * formatted via Intl.NumberFormat so they localize together. A null, undefined, or
 * NaN input yields an empty string, so a caller can render the result directly
 * without guarding — the empty result reads as "uses the activity default".
 */
export const formatSpeed = (
  kmh: number | null | undefined,
  { length, locale }: UnitFormatOptions,
): string => {
  if (!isFiniteNumber(kmh)) {
    return "";
  }

  const isMetric = length === "metric";
  const value = isMetric ? kmh : kmh / KILOMETERS_PER_MILE;

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
    style: "unit",
    unit: isMetric ? "kilometer-per-hour" : "mile-per-hour",
    unitDisplay: "short",
  }).format(value);
};

/**
 * parseSpeedToKmh converts a travel speed typed in the reader's speed unit
 * (km/h for metric, mph for imperial) back into the km/h the wire carries. It is
 * the inverse of formatSpeed, reusing the same KILOMETERS_PER_MILE factor so a
 * value formatted and re-parsed round-trips, and is the helper the suggest speed
 * control reads its NumberInput through so the session always holds km/h
 * regardless of the displayed unit.
 */
export const parseSpeedToKmh = (value: number, length: UnitLength): number =>
  length === "metric" ? value : value * KILOMETERS_PER_MILE;

/**
 * formatTemperature renders a temperature given in degrees Celsius (the overlay's
 * native unit) as a localized, unit-suffixed string in the reader's measurement
 * system: metric keeps Celsius, imperial converts to Fahrenheit
 * (`celsius × 9/5 + 32`), each rounded to a whole degree — sub-degree precision is
 * noise for a weather readout. The number and unit are both formatted via
 * Intl.NumberFormat so they localize together. A null, undefined, or NaN input
 * yields an empty string, so a caller can render the result directly without
 * guarding.
 */
export const formatTemperature = (
  celsius: number | null | undefined,
  { length, locale }: UnitFormatOptions,
): string => {
  if (!isFiniteNumber(celsius)) {
    return "";
  }

  const isMetric = length === "metric";
  const value = isMetric
    ? celsius
    : celsius * CELSIUS_TO_FAHRENHEIT_RATIO + CELSIUS_TO_FAHRENHEIT_OFFSET;

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    style: "unit",
    unit: isMetric ? "celsius" : "fahrenheit",
    unitDisplay: "short",
  }).format(value);
};

/**
 * formatHumidity renders a relative-humidity percentage as a localized,
 * percent-suffixed string rounded to a whole number. Humidity is a dimensionless
 * ratio, so there is no metric/imperial conversion — only the locale drives the
 * decimal separator and the percent unit's spacing. A null, undefined, or NaN
 * input yields an empty string, so a caller can render the result directly without
 * guarding. The full UnitFormatOptions is accepted for a uniform call signature
 * with the other overlay formatters; only the locale is read.
 */
export const formatHumidity = (
  value: number | null | undefined,
  { locale }: UnitFormatOptions,
): string => {
  if (!isFiniteNumber(value)) {
    return "";
  }

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    style: "unit",
    unit: "percent",
    unitDisplay: "short",
  }).format(value);
};

/**
 * formatSnowCentimeters renders a snow measurement given in centimeters as a
 * localized, unit-suffixed string in the reader's measurement system: metric keeps
 * centimeters rounded to a whole number, imperial converts to inches
 * (`centimeters × 0.393701`) rounded to one decimal (an inch is coarse enough that
 * a whole-number inch would lose a light dusting). The number and unit are both
 * formatted via Intl.NumberFormat so they localize together. The shared centimeter
 * core of formatSnowfall and formatSnowDepth, so both depths and accumulations read
 * with the same unit handling.
 */
const formatSnowCentimeters = (
  centimeters: number,
  { length, locale }: UnitFormatOptions,
): string => {
  const isMetric = length === "metric";
  const value = isMetric ? centimeters : centimeters * INCHES_PER_CENTIMETER;

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: isMetric ? 0 : 1,
    style: "unit",
    unit: isMetric ? "centimeter" : "inch",
    unitDisplay: "short",
  }).format(value);
};

/**
 * formatSnowfall renders a snowfall accumulation given in centimeters (the
 * overlay's native unit) as a localized, unit-suffixed string in the reader's
 * measurement system: metric keeps centimeters, imperial converts to inches. A
 * null, undefined, or NaN input yields an empty string, so a caller can render the
 * result directly without guarding.
 */
export const formatSnowfall = (
  centimeters: number | null | undefined,
  options: UnitFormatOptions,
): string => {
  if (!isFiniteNumber(centimeters)) {
    return "";
  }

  return formatSnowCentimeters(centimeters, options);
};

/**
 * formatSnowDepth renders a snow depth given in meters (the overlay's native unit)
 * as a localized, unit-suffixed string in the reader's measurement system: the
 * meters are converted to centimeters first, then metric keeps centimeters and
 * imperial converts to inches. A null, undefined, or NaN input yields an empty
 * string, so a caller can render the result directly without guarding.
 */
export const formatSnowDepth = (
  meters: number | null | undefined,
  options: UnitFormatOptions,
): string => {
  if (!isFiniteNumber(meters)) {
    return "";
  }

  return formatSnowCentimeters(meters * CENTIMETERS_PER_METER, options);
};

/**
 * resolveSpeedKmh resolves the travel speed (km/h) a segment is actually clocked
 * at: its own override when set to a positive value, otherwise the activity's
 * default speed. It mirrors the backend's speed resolution (an override of null or
 * zero falls back to the activity default), so a client estimate and the server
 * recompute agree. Returns null when neither a positive override nor an activity
 * default is available (the catalog has not resolved), so the caller cannot derive
 * a duration yet.
 */
export const resolveSpeedKmh = (
  overrideKmh: number | null,
  activityDefaultKmh: number | null,
): number | null => (overrideKmh != null && overrideKmh > 0 ? overrideKmh : activityDefaultKmh);

/**
 * DurationForSpeedInput carries the inputs durationForSpeed scales a duration
 * from: the segment's base duration (seconds) and the base speed (km/h) it was
 * computed at, the segment distance (meters) for the no-base fallback, and the
 * target speed (km/h) to rescale to. Every field is nullable so an incomplete
 * segment (no metrics yet, catalog still loading) flows through to a null result.
 */
export interface DurationForSpeedInput {
  baseDurationSeconds: number | null;
  baseSpeedKmh: number | null;
  distanceMeters: number | null;
  targetSpeedKmh: number | null;
}

/**
 * durationForSpeed rescales a segment's travel duration (seconds) for a new speed
 * without re-deriving it from the geometry. It encodes only the stable
 * `duration ∝ 1/speed` invariant for a fixed path — the same physical relationship
 * the backend applies — so a known base duration scales by the speed ratio
 * (`base × baseSpeed / target`). When there is no usable base (a fresh segment with
 * no computed duration) it falls back to the straight distance/speed estimate
 * (`distance / (target × 1000/3600)`). It is NOT the directions-vs-linestring
 * formula, keeping the client coupling to the backend math minimal. Returns null
 * when the target speed is absent or non-positive, or when neither a base duration
 * nor a distance can produce a figure.
 */
export const durationForSpeed = ({
  baseDurationSeconds,
  baseSpeedKmh,
  distanceMeters,
  targetSpeedKmh,
}: DurationForSpeedInput): number | null => {
  if (targetSpeedKmh == null || targetSpeedKmh <= 0) {
    return null;
  }

  if (
    baseDurationSeconds != null &&
    baseDurationSeconds > 0 &&
    baseSpeedKmh != null &&
    baseSpeedKmh > 0
  ) {
    return (baseDurationSeconds * baseSpeedKmh) / targetSpeedKmh;
  }

  if (distanceMeters != null && distanceMeters > 0) {
    return distanceMeters / ((targetSpeedKmh * METERS_PER_KILOMETER) / SECONDS_PER_HOUR);
  }

  return null;
};

/** SECONDS_PER_HOUR splits a duration in seconds into whole hours. */
const SECONDS_PER_HOUR = 3600;

/** SECONDS_PER_MINUTE rounds the leftover seconds (after whole hours) into minutes. */
const SECONDS_PER_MINUTE = 60;

/** MINUTES_PER_HOUR carries a rounded 60-minute remainder up into the next hour. */
const MINUTES_PER_HOUR = 60;

/**
 * formatDuration renders an estimated travel duration given in seconds as a
 * localized "Hh Mmin" / "Mmin" string in the viewer's locale: whole hours and
 * rounded minutes, each formatted via Intl.NumberFormat with its narrow hour/minute
 * unit so the number and unit localize together and join with a single space. When
 * the hours are non-zero the minutes part is dropped if it rounds to zero (a flat
 * "2 hr"); under an hour only the minutes show. The minute rounding carries — a
 * remainder that rounds to 60 rolls up into the next hour rather than printing
 * "60 min". A null, undefined, NaN, negative, or zero input yields an empty string,
 * so a caller can render the result directly without guarding and a route with no
 * estimate reads as absent rather than "0 min".
 */
export const formatDuration = (
  seconds: number | null | undefined,
  { locale }: Pick<UnitFormatOptions, "locale">,
): string => {
  if (!isFiniteNumber(seconds) || seconds <= 0) {
    return "";
  }

  let hours = Math.floor(seconds / SECONDS_PER_HOUR);
  let minutes = Math.round((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  // A leftover that rounds up to a full hour rolls into the hours so the minutes
  // part never prints "60 min".
  if (minutes === MINUTES_PER_HOUR) {
    hours += 1;
    minutes = 0;
  }

  const formatPart = (value: number, unit: "hour" | "minute"): string =>
    new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
      style: "unit",
      unit,
      unitDisplay: "narrow",
    }).format(value);

  if (hours > 0) {
    // Drop a zero-minute remainder so a whole-hour duration reads as just the hours.
    return minutes === 0
      ? formatPart(hours, "hour")
      : `${formatPart(hours, "hour")} ${formatPart(minutes, "minute")}`;
  }

  return formatPart(minutes, "minute");
};

/**
 * formatSlope renders a slope given in degrees as a localized, unit-suffixed
 * string in the viewer's slope unit: "degrees" formats the angle directly with a
 * degree sign, "percentage" converts the angle to a rise-over-run grade
 * (tan(angle) × 100) and suffixes a percent sign. Both round to a whole number —
 * sub-degree/sub-percent precision is noise for a slope readout — and the sign is
 * preserved so a descent reads negative. The number is formatted via
 * Intl.NumberFormat so the decimal separator and the unit localize together. A
 * null, undefined, or NaN input yields an empty string, so a caller can render the
 * result directly without guarding.
 */
export const formatSlope = (
  degrees: number | null | undefined,
  { locale, slope }: UnitFormatOptions,
): string => {
  if (!isFiniteNumber(degrees)) {
    return "";
  }

  if (slope === "percentage") {
    const percent = Math.tan(degrees * DEGREES_TO_RADIANS) * 100;

    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
      style: "unit",
      unit: "percent",
      unitDisplay: "short",
    }).format(percent);
  }

  // Intl.NumberFormat has no "degree" angle unit, so the degree sign is appended
  // to the localized number directly.
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(degrees)}°`;
};

/**
 * AspectCardinal names one of the eight compass headings the slope-aspect bearing
 * resolves to. The cardinal is paired with the numeric bearing in the rendered
 * tooltip so a viewer reads both ("120° (SE)") — the number is precise, the
 * cardinal is glanceable.
 */
export type AspectCardinal = "N" | "NE" | "E" | "SE" | "S" | "SW" | "W" | "NW";

/**
 * ASPECT_CARDINALS lists the eight compass headings in clockwise order starting
 * at north, so a bearing wraps cleanly: every 45° window picks the next entry,
 * and the lookup folds the bearing modulo 360° before sampling the table.
 */
const ASPECT_CARDINALS: AspectCardinal[] = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

/**
 * aspectCardinal maps a 0..360° compass bearing to the 8-point cardinal it falls
 * in: every 45° window is one cardinal, with the window centered on the
 * cardinal's heading (north spans 337.5°..22.5°, NE 22.5°..67.5°, …). A null,
 * undefined, or non-finite bearing yields null so the caller can drop the
 * cardinal row.
 */
export const aspectCardinal = (degrees: number | null | undefined): AspectCardinal | null => {
  if (!isFiniteNumber(degrees)) {
    return null;
  }

  // Fold to [0, 360) then offset by half a cardinal window so a value sitting
  // on a window edge rounds toward the cardinal it is closer to.
  const folded = ((degrees % 360) + 360) % 360;
  const index = Math.floor(((folded + 22.5) % 360) / 45);

  return ASPECT_CARDINALS[index] ?? null;
};

/**
 * formatAltitudeRange renders a min/max altitude pair (both in meters) as a single
 * localized "low – high" string in the viewer's length unit, joining the two
 * elevations with a spaced en dash. It reuses formatElevation for each bound so the
 * unit conversion and rounding match a standalone elevation. The spaced en dash
 * matches the slope-range separator so the two ranges read consistently. When either
 * bound is null, undefined, or NaN there is no range to show, so it yields an empty
 * string and the caller can render the result directly without guarding.
 */
export const formatAltitudeRange = (
  min: number | null | undefined,
  max: number | null | undefined,
  options: UnitFormatOptions,
): string => {
  if (!isFiniteNumber(min) || !isFiniteNumber(max)) {
    return "";
  }

  return `${formatElevation(min, options)} – ${formatElevation(max, options)}`;
};
